import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useBrandingTheme } from '@/hooks/stores/useBrandingTheme';
import { MainMenuItem } from '@/types/common';
import {
  ADVISOR_ADMIN_MENU,
  ADVISOR_MENU,
  createBankApplicationMenu,
  STAFF_MENU,
} from '@/constants/menu';
import { ROUTES } from '@/constants/routes';
import { useGetUser } from '@/hooks/requests/useUser';
import { UserRoles } from '@/types/user';
import { useGetBankApplicationCounts } from '@/hooks/requests/useBankApplications';
import { SVLink } from './core';
import { CopyAll } from '@mui/icons-material';
import showToast from '@/utils/toast';

type MainMenuButtonProps = {
  item: MainMenuItem;
  isActive: boolean;
  isSubmenu: boolean;
  setOpen: (value: React.SetStateAction<boolean>) => void;
};

function MainMenuButton({
  item,
  isActive,
  isSubmenu,
  setOpen,
}: MainMenuButtonProps) {
  const { brandingTheme, mode } = useBrandingTheme();
  const navigate = useNavigate();
  const defaultColor = mode === 'light' ? 'black' : 'white';

  return (
    <Button
      sx={{
        color: !isSubmenu
          ? defaultColor
          : isActive
            ? brandingTheme.primaryColor
            : defaultColor,
        fontWeight: isActive || isSubmenu ? 'bold' : 'normal',
        fontSize: '20px',
        textAlign: 'left',
        textTransform: 'none',
        borderLeft: isSubmenu
          ? 'none'
          : `4px solid ${isActive ? brandingTheme.primaryColor : 'none'}`,
        padding: '5px 10px 5px 20px',
        borderRadius: '0',
        width: '100%',
        justifyContent: 'flex-start',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      }}
      onClick={() => {
        navigate(item.route);
        setOpen(false);
      }}>
      {item.label}
    </Button>
  );
}

type MainMenuProps = {
  setOpen: (value: React.SetStateAction<boolean>) => void;
};

export default function MainMenu({ setOpen }: MainMenuProps) {
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const { user } = useGetUser();
  const isStaff = user?.role === UserRoles.STAFF;
  const isAdvisorAdmin = user?.role === UserRoles.TEAMMATE_ADMIN;
  const { aggregateInfo } = useGetBankApplicationCounts();
  const advisorUrl = user?.teammateInfo?.advisorUrl;

  const baseMenu = isStaff
    ? STAFF_MENU
    : isAdvisorAdmin
      ? ADVISOR_ADMIN_MENU
      : ADVISOR_MENU;
  const bankApplicationMenu = createBankApplicationMenu(aggregateInfo);

  const mainMenu = bankApplicationMenu
    ? baseMenu.map(item => {
        if (item.key === 'bank-account-applications') {
          return bankApplicationMenu;
        }
        return item;
      })
    : baseMenu;

  const location = useLocation();
  const currentPath = location.pathname;

  function copyUrl() {
    navigator.clipboard.writeText(advisorUrl || '');
    showToast({
      type: 'success',
      data: { content: 'Personal Banking Link copied to clipboard' },
    });
  }

  return (
    <Box sx={{ position: 'sticky', top: '16px', left: 0 }}>
      {mainMenu.map(item => {
        const isActive =
          item.route === ROUTES.HOMEPAGE
            ? item.route === currentPath
            : item.activeRoutes.filter(route => currentPath.includes(route))
                .length >= 1;
        const showSubpages = isActive && item.subpages;

        return (
          <Box key={item.key}>
            <MainMenuButton
              item={item}
              isActive={isActive || false}
              isSubmenu={false}
              setOpen={setOpen}
            />
            {showSubpages && (
              <Box sx={{ paddingLeft: '20px' }}>
                {item.subpages?.map(subpageItem => {
                  const isSubpageActive =
                    subpageItem.activeRoutes.filter(route =>
                      currentPath.includes(route),
                    ).length >= 1;

                  return (
                    <MainMenuButton
                      key={subpageItem.key}
                      item={subpageItem}
                      isActive={isSubpageActive}
                      isSubmenu
                      setOpen={setOpen}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
      {!isStaff && advisorUrl && (
        <Box
          sx={{
            pl: '15px',
            pr: '15px',
            position: 'fixed',
            bottom: '25px',
            left: isLargeScreen ? 0 : null,
            right: isLargeScreen ? null : 0,
            width: isLargeScreen ? '290px' : '250px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
          }}>
          <Box
            sx={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}>
            <Typography fontWeight="bold">Personal Banking Link</Typography>
            <SVLink to={advisorUrl} target="_blank">
              {advisorUrl}
            </SVLink>
          </Box>
          <IconButton onClick={copyUrl}>
            <CopyAll />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
